import React, { useMemo } from 'react';
import {
    Drawer,
    Stack,
    Typography,
    IconButton,
    Card,
    CardContent,
    Divider,
    Alert,
    Skeleton,
} from '@mui/material';
import { Close as CloseIcon, Payment as PaymentIcon } from '@mui/icons-material';
import moment from 'moment';
import { t } from 'i18next';
import { useSalesDetails } from '@/hooks/api/pos/sales/useSalesDetails';
import { formatCurrency } from '@/scenes/POS/Core/pos.utils';
import { GetApiSalesDetailsId200TransactionsItem } from '@/shared/api/models';

interface PaymentTransactionsDrawerProps {
    open: boolean;
    onClose: () => void;
    salesId: string | null;
}

const getPaymentTypeDisplay = (type: string): string => {
    const types: Record<string, string> = {
        CASH: t('POS.Cash'),
        CARD: t('POS.Card'),
        MOBILE_PAY: t('POS.MobilePay'),
        BANK_TRANSFER: t('POS.BankTransfer'),
        OUTSTANDING: t('POS.Outstanding'),
        GIFT_CARD: t('POS.GiftCard'),
        BONUS: t('POS.Bonus'),
        OTHER: t('POS.Other'),
        ECOMMERCE: t('POS.EcommercePayment'),
        DEPOSIT: t('POS.DepositPayment'),
        CARD_CREDIT: `${t('POS.Card')} (${t('POS.Refund')})`,
    };

    return types[type] || type;
};

const isRefundTransaction = (transaction: GetApiSalesDetailsId200TransactionsItem): boolean => {
    const transactionType = (transaction.transactionType || '').toUpperCase();
    return transactionType.includes('REFUND') || (transaction.amount ?? 0) < 0;
};

const isCardPayment = (transaction: GetApiSalesDetailsId200TransactionsItem): boolean =>
    (transaction.paymentType || '').toUpperCase() === 'CARD';

export default function PaymentTransactionsDrawer({ open, onClose, salesId }: PaymentTransactionsDrawerProps) {
    const { data: salesData, isLoading, error } = useSalesDetails(salesId || '', open && !!salesId);

    const sortedTransactions = useMemo(() => {
        const transactions = salesData?.transactions ?? [];
        return [...transactions].sort((a, b) => {
            const dateA = moment(a.createdAt);
            const dateB = moment(b.createdAt);
            if (!dateA.isValid() || !dateB.isValid()) {
                return 0;
            }
            return dateA.valueOf() - dateB.valueOf();
        });
    }, [salesData?.transactions]);

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 480 } } }}>
            <Stack sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <PaymentIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                            {t('POS.PaymentTransactions')}
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </Stack>

                {salesData?.invoiceId && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t('POS.InvoiceNumber')}: {salesData.invoiceId}
                    </Typography>
                )}

                {isLoading && (
                    <Stack spacing={2}>
                        {[1, 2, 3].map((item) => (
                            <Card key={item} elevation={1} sx={{ borderLeft: 4, borderColor: 'grey.300' }}>
                                <CardContent>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Skeleton variant="text" width={120} height={24} />
                                            <Skeleton variant="text" width={100} height={20} />
                                        </Stack>
                                        <Divider />
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Skeleton variant="text" width={80} height={20} />
                                            <Skeleton variant="text" width={60} height={24} />
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}

                {error && <Alert severity="error">{t('POS.FetchOnlinePaymentsFailed')}</Alert>}

                {!isLoading && !error && (
                    <Stack spacing={2} sx={{ overflowY: 'auto' }}>
                        {sortedTransactions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {t('POS.PrevSaleNoData')}
                            </Typography>
                        ) : (
                            sortedTransactions.map((transaction: GetApiSalesDetailsId200TransactionsItem, index) => {
                                const isRefund = isRefundTransaction(transaction);
                                const amountColor = isRefund ? 'error.main' : 'success.main';
                                const amountPrefix = isRefund ? '-' : '+';
                                const displayAmount = formatCurrency(Math.abs(transaction.amount ?? 0));

                                return (
                                    <Card
                                        key={transaction.id || index}
                                        elevation={1}
                                        sx={{
                                            borderLeft: 4,
                                            borderColor: amountColor,
                                        }}
                                    >
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        {getPaymentTypeDisplay(transaction.paymentType)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {transaction.createdAt
                                                            ? moment(transaction.createdAt).format('DD/MM-YYYY HH:mm')
                                                            : '-'}
                                                    </Typography>
                                                </Stack>
                                                <Divider />
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('Common.Amount')}
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight={600} color={amountColor}>
                                                        {amountPrefix}
                                                        {displayAmount}
                                                    </Typography>
                                                </Stack>
                                                {!isRefund &&
                                                    transaction.refundAmount != null &&
                                                    transaction.refundAmount > 0 && (
                                                        <Stack direction="row" justifyContent="space-between">
                                                            <Typography variant="body2" color="text.secondary">
                                                                {t('POS.RefundAmount')}
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                fontWeight={500}
                                                                color="error.main"
                                                            >
                                                                -{formatCurrency(transaction.refundAmount)}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                {isCardPayment(transaction) && transaction.cardType && (
                                                    <Stack direction="row" justifyContent="space-between">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('POS.CardType')}
                                                        </Typography>
                                                        <Typography variant="body2">{transaction.cardType}</Typography>
                                                    </Stack>
                                                )}
                                                {isCardPayment(transaction) && transaction.cardFourDigit && (
                                                    <Stack direction="row" justifyContent="space-between">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('POS.Card')}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            **** {transaction.cardFourDigit}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                                {transaction.txStatus && (
                                                    <Stack direction="row" justifyContent="space-between">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('Common.Status')}
                                                        </Typography>
                                                        <Typography variant="body2">{transaction.txStatus}</Typography>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                );
                            })
                        )}
                    </Stack>
                )}
            </Stack>
        </Drawer>
    );
}
